import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { InfiniteList } from "@/components/admin/infinite-list";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import type { Sale } from "../types";

const filters = [<SearchInput source="q" alwaysOn />];

export const MobileSalesList = () => {
  const translate = useTranslate();
  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold">
          {translate("resources.sales.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <InfiniteList
          filters={filters}
          actions={false}
          sort={{ field: "first_name", order: "ASC" }}
          perPage={50}
        >
          <MobileSalesListContent />
        </InfiniteList>
      </MobileContent>
    </>
  );
};

const MobileSalesListContent = () => {
  const translate = useTranslate();
  const { taskCategories } = useConfigurationContext();
  const { data, isPending } = useListContext<Sale>();
  const { identity } = useGetIdentity();
  const categories = taskCategories ?? defaultTaskCategories;

  if (isPending) return null;

  return (
    <div className="divide-y">
      {data?.map((sale) => {
        const roleLabel =
          categories.find((c) => c.value === (sale.role ?? "sales"))?.label ??
          sale.role ??
          "Sales";
        const isSelf = sale.id === identity?.id;

        return (
          <div
            key={sale.id}
            className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold">
              {sale.first_name?.[0]}
              {sale.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {sale.first_name} {sale.last_name}
                {isSelf && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({translate("resources.sales.you", { _: "you" })})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                <span className="truncate">{sale.email}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge variant="secondary" className="text-xs">
                {roleLabel}
              </Badge>
              {sale.administrator && (
                <Badge variant="outline" className="text-xs">
                  {translate("resources.sales.fields.administrator")}
                </Badge>
              )}
              {sale.disabled && (
                <Badge variant="destructive" className="text-xs">
                  {translate("resources.sales.fields.disabled")}
                </Badge>
              )}
            </div>
          </div>
        );
      })}
      {(!data || data.length === 0) && (
        <div className="p-4 text-muted-foreground text-sm">
          {translate("resources.sales.empty", { _: "No team members found." })}
        </div>
      )}
      <InfinitePagination />
    </div>
  );
};
